import { Phone, Mail, MapPin, Linkedin } from "lucide-react";
import { getOrgConfig } from "../../utils/organizationConfigUtil";

const SOCIAL_LINKS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/skytpo",
    icon: Linkedin,
  },
];

const RESOURCES_LINKS = [
  { label: "SkyTPO.com", href: "https://skytpo.com/" },
  { label: "About", href: "https://skytpo.com/about" },
  { label: "Products", href: "https://skytpo.com/products" },
  { label: "State Licensing", href: "https://skytpo.com/licenses" },
  { label: "Broker Resources", href: "https://skytpo.com/resources" },
  { label: "Guidelines & Matrices", href: "https://skytpo.com/guidelines" },
];
const MAP_HREF =
  "https://www.google.com/maps/search/?api=1&query=207+Goode+Ave,+Ste+380,+Glendale,+CA+91203";

const CONTACTUS_LINKS = [
  { label: "(818) 479-8770", href: "tel:8184798770" },
  { label: "hello@skytpo.com", href: "mailto:hello@skytpo.com" },
  { label: "207 Goode Ave, Ste 380", href: MAP_HREF, gapBefore: true },
  { label: "Glendale, CA 91203", href: MAP_HREF },
  { label: "United States", href: MAP_HREF },
  { label: "NMLS ID #150086" },
];

const PHONE_LINKS = [
  { label: "(818) 479-8770", href: "tel:8184798770" },
];
// Keep mouse clicks from leaving focus (and the browser focus ring) on links.
// Keyboard focus is unaffected, so focus-visible styles still work.
const preventFocusOnClick = (e) => e.preventDefault();

const FOOTER_ITEM_CLASS = "text-sm font-medium text-white/90 no-underline";

function FooterNavColumn({ id, title, title2, links }) {
  return (
    <nav aria-labelledby={id}>
      <h2 id={id} className="font-semibold text-lg mb-4 text-brand-primary">
        {title}
        {title2 && <span className="block">{title2}</span>}
      </h2>
      <ul className="space-y-2">
        {links.map((link) => (
          <li key={link.label} className={link.gapBefore ? "!mt-6" : undefined}>
            {link.href ? (
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                onMouseDown={preventFocusOnClick}
                className={`${FOOTER_ITEM_CLASS} hover:text-white hover:no-underline transition-colors focus:outline-none`}
              >
                {link.label}
              </a>
            ) : (
              <span className={FOOTER_ITEM_CLASS}>{link.label}</span>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default function MMCLendingFooter() {
  const org = getOrgConfig();
  const logoSrc = org?.footerLogo ?? org?.logo;
  const logoAlt = `${org?.tpoName ?? org?.companyName} logo`;
  const homeHref = org?.siteDomain ? `https://${org.siteDomain}/` : "/";

  return (
    <footer className="bg-brand-primary text-white" role="contentinfo">
      <div className="max-w-8xl mx-auto px-4 py-10 lg:py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <a
              href={homeHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${org?.tpoName ?? org?.companyName} - Go to homepage`}
              onMouseDown={preventFocusOnClick}
              className="inline-block no-underline focus:outline-none"
            >
              <img
                src={logoSrc}
                alt={logoAlt}
                width={218}
                height={116}
                className="w-[180px] h-auto object-contain object-left"
              />
            </a>
            <hr className="w-[180px] mt-4 border-0 border-t border-white/40" />

            <div className="flex items-center justify-center space-x-4 mt-6">
              {SOCIAL_LINKS.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Follow us on ${social.label}`}
                    onMouseDown={preventFocusOnClick}
                    className="text-white hover:text-white/80 hover:no-underline transition-colors focus:outline-none"
                  >
                    <Icon size={20} aria-hidden="true" />
                  </a>
                );
              })}
            </div>
          </div>
          <FooterNavColumn id="footer-resources" title="Resources" links={RESOURCES_LINKS} />
          <FooterNavColumn id="footer-contact" title="Contact Us" links={CONTACTUS_LINKS} />
          <FooterNavColumn id="footer-support" title="For Customer Support/" title2="Loan Payment Options" links={PHONE_LINKS} />
        </div>
      </div>
    </footer>
  );
}
